// src/services/AuthService.js
import storage from './StorageAdapter';
import auditService from './AuditService';

// Default demo users — seeded on first use when no local users are configured.
// Replace by switching provider to 'jwt' and pointing to a backend endpoint.
const DEFAULT_USERS = [
  { email: 'simon@mertil.se', password: 'mertil2025', role: 'admin', name: 'Simon' }
];

export const SSO_PROVIDERS = ['oidc', 'saml'];

const DEFAULT_OIDC = {
  discoveryUrl: '',
  clientId: '',
  clientSecret: '',
  redirectUri: '',
  scope: 'openid profile email',
};

const DEFAULT_SAML = {
  entityId: '',
  idpMetadataUrl: '',
  acsUrl: '',
  nameIdFormat: 'emailAddress',
};

class AuthService {
  constructor() {
    this.STORAGE_KEY = 'rms_auth_config';
    this.DEFAULTS = {
      provider: 'local', // 'local' | 'jwt' | 'oidc' | 'saml'
      jwt: {
        endpoint: '',
        clientId: ''
      },
      oidc: { ...DEFAULT_OIDC },
      saml: { ...DEFAULT_SAML },
    };
  }

  // Hämta auth-konfiguration
  getAuthConfig() {
    const stored = storage.getJSON(this.STORAGE_KEY, {});
    return {
      ...this.DEFAULTS,
      ...stored,
      oidc: { ...DEFAULT_OIDC, ...(stored.oidc || {}) },
      saml: { ...DEFAULT_SAML, ...(stored.saml || {}) },
    };
  }

  // Spara auth-konfiguration
  async saveAuthConfig(config) {
    const merged = { ...this.getAuthConfig(), ...config };
    await storage.setJSON(this.STORAGE_KEY, merged);
    auditService.logUpdate('Admin', 'AuthConfig', 'system', {
      provider: config.provider
    }, 'Auth configuration updated');
    return { success: true };
  }

  // Spara SSO-konfiguration (OIDC eller SAML)
  async saveSSOConfig(provider, config) {
    if (!SSO_PROVIDERS.includes(provider)) {
      return { success: false, error: 'invalid_provider' };
    }
    const current = this.getAuthConfig();
    const updated = { ...current, [provider]: { ...current[provider], ...config } };
    await storage.setJSON(this.STORAGE_KEY, updated);
    auditService.logUpdate('Admin', 'SSOConfig', 'system', { provider }, 'SSO configuration updated');
    return { success: true };
  }

  // Aktivera en SSO-provider som aktiv auth-metod
  async enableSSOProvider(provider) {
    if (!SSO_PROVIDERS.includes(provider) && provider !== 'local' && provider !== 'jwt') {
      return { success: false, error: 'invalid_provider' };
    }
    return this.saveAuthConfig({ provider });
  }

  // Simulera anslutningstest mot SSO-provider (mock — ingen riktig IdP-kommunikation)
  async testSSOConnection(provider, config) {
    // Validation: check required fields
    if (provider === 'oidc') {
      if (!config.discoveryUrl || !config.clientId) {
        return { success: false, error: 'missing_required_fields' };
      }
      // Mock: simluera lyckad anslutning om URL har rätt format
      if (!config.discoveryUrl.startsWith('https://')) {
        return { success: false, error: 'invalid_discovery_url' };
      }
      return { success: true, message: 'oidc_connection_ok', metadata: { issuer: config.discoveryUrl } };
    }

    if (provider === 'saml') {
      if (!config.entityId || !config.idpMetadataUrl) {
        return { success: false, error: 'missing_required_fields' };
      }
      if (!config.idpMetadataUrl.startsWith('https://')) {
        return { success: false, error: 'invalid_metadata_url' };
      }
      return { success: true, message: 'saml_connection_ok', metadata: { entityId: config.entityId } };
    }

    return { success: false, error: 'unsupported_provider' };
  }

  // Hämta lokala användare (faller tillbaka på DEFAULT_USERS)
  getLocalUsers() {
    const config = this.getAuthConfig();
    return config.localUsers || DEFAULT_USERS;
  }

  // Hämta aktiv auth-provider
  getProvider() {
    return this.getAuthConfig().provider;
  }

  // Kontrollera om JWT-endpoint är konfigurerad
  isJWTConfigured() {
    const config = this.getAuthConfig();
    return config.provider === 'jwt' && Boolean(config.jwt && config.jwt.endpoint);
  }

  // Kontrollera om OIDC är konfigurerad
  isOIDCConfigured() {
    const config = this.getAuthConfig();
    return Boolean(config.oidc?.discoveryUrl && config.oidc?.clientId);
  }

  // Kontrollera om SAML är konfigurerad
  isSAMLConfigured() {
    const config = this.getAuthConfig();
    return Boolean(config.saml?.entityId && config.saml?.idpMetadataUrl);
  }

  // Validera inloggning mot konfigurerad provider
  async authenticate(email, password) {
    const config = this.getAuthConfig();

    if (config.provider === 'local') {
      const users = this.getLocalUsers();
      const user = users.find(
        u => u.email === email && u.password === password
      );
      if (user) {
        return {
          success: true,
          user: { email: user.email, role: user.role, name: user.name },
          token: null
        };
      }
      return { success: false };
    }

    if (config.provider === 'jwt') {
      // JWT-autentisering — placeholder tills backend-integration är implementerad
      return { success: false, error: 'jwt_not_configured' };
    }

    if (config.provider === 'oidc') {
      // OIDC-autentisering — omdirigerar till IdP (hanteras externt)
      return { success: false, error: 'sso_redirect_required', provider: 'oidc' };
    }

    if (config.provider === 'saml') {
      // SAML-autentisering — omdirigerar till IdP (hanteras externt)
      return { success: false, error: 'sso_redirect_required', provider: 'saml' };
    }

    return { success: false };
  }

  // Spara eller uppdatera lösenord för en lokal användare
  // Om localUsers inte finns än, seedas från DEFAULT_USERS för att bevara befintliga konton
  async setUserPassword(email, password) {
    const config = this.getAuthConfig();
    const users = [...(config.localUsers || DEFAULT_USERS)];
    const idx = users.findIndex(u => u.email === email);
    if (idx >= 0) {
      users[idx] = { ...users[idx], password };
    } else {
      users.push({ email, password, role: 'local', name: email });
    }
    await storage.setJSON(this.STORAGE_KEY, { ...config, localUsers: users });
    return { success: true };
  }

  // JWT token-validering (placeholder för framtida backend-integration)
  authenticateWithJWT(token) {
    // TODO: Anropa config.jwt.endpoint + '/auth/validate' med token
    return Promise.resolve({ success: false, error: 'jwt_not_implemented' });
  }
}

const authService = new AuthService();

export default authService;
